import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShoppingBag, Store, Users, TrendingUp, CheckCircle, ArrowRight, Sparkles, Globe, BarChart3, Shield, Zap } from "lucide-react";
import { Link } from "wouter";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

const FEATURES = [
  { icon: Store, title: "Your Own Online Store", desc: "Each RetailPulse merchant gets a beautiful, branded storefront — list your products and start selling online in minutes." },
  { icon: Globe, title: "Shared Marketplace", desc: "Your products also appear on the RetailPulse Marketplace, reaching thousands of shoppers across Nigeria." },
  { icon: BarChart3, title: "Unified Dashboard", desc: "Online and in-store sales in one place. Inventory syncs automatically so you never oversell." },
  { icon: Zap, title: "Live Inventory", desc: "Keep online availability aligned with your canonical POS inventory so customers see current stock." },
  { icon: Users, title: "Built for Retailers", desc: "Give customers a simple way to discover your products while you manage everything from one dashboard." },
  { icon: Shield, title: "Secure Checkout", desc: "Customer and merchant access stay protected with authenticated account controls and payment verification." },
];

const BUSINESS_TYPES = [
  { value: "fashion", label: "Fashion & Clothing" },
  { value: "food", label: "Food & Groceries" },
  { value: "supermarket", label: "Supermarket / FMCG" },
  { value: "electronics", label: "Electronics & Gadgets" },
  { value: "beauty", label: "Beauty & Personal Care" },
  { value: "other", label: "Other" },
];

export default function EcommerceComingSoon() {
  const [form, setForm] = useState({ name: "", email: "", businessType: "", community: "", message: "" });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch(`${BASE}/api/ecommerce/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, businessType: form.businessType, community: form.community, message: form.message }),
      });
      const data = await res.json();
      if (!res.ok) { setErrorMsg(data.error || "Something went wrong"); setStatus("error"); return; }
      setStatus("success");
    } catch {
      setErrorMsg("Network error. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-slate-100 bg-white/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-2.5 cursor-pointer">
              <img src={LOGO} alt="RetailPulse" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">RetailPulse</span>
            </div>
          </Link>
          <Link href="/signup">
            <Button size="sm" variant="outline">Get POS Free Trial</Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(99,102,241,0.3),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-24 md:py-36 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm mb-8 backdrop-blur">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-slate-200">Coming Soon — E-Commerce for Nigerian Retailers</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Sell Online.<br />
            <span className="text-indigo-400">Keep More of What You Earn.</span>
          </h1>
          <p className="text-slate-300 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
             RetailPulse E-Commerce is coming. Your own online store. A shared Nigerian marketplace. Inventory synced with your POS. Storefront access is available at no extra charge.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#waitlist">
              <Button size="lg" className="bg-indigo-500 hover:bg-indigo-400 h-12 px-8 text-base gap-2">
                Join the Waitlist <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
            <Link href="/signup">
              <Button size="lg" variant="outline" className="h-12 px-8 text-base border-white/20 text-white hover:bg-white/10">
                Get POS Free Trial First
              </Button>
            </Link>
          </div>

          {/* Mini stats */}
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg mx-auto">
              {[
               { v: "₦0", l: "Storefront access" },
               { v: "Live", l: "POS inventory sync" },
               { v: "10 min", l: "To go live" },
            ].map(s => (
              <div key={s.l} className="text-center">
                <div className="text-2xl font-bold text-white">{s.v}</div>
                <div className="text-xs text-slate-400 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Everything You Need to Sell Online</h2>
            <p className="text-slate-500 text-lg">Built on top of your existing RetailPulse POS — no extra setup.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(f => (
              <div key={f.title} className="rounded-2xl border border-slate-100 p-6 hover:border-indigo-200 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gradient-to-br from-indigo-950 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold mb-3">How It Will Work</h2>
          <p className="text-slate-400 mb-14 text-lg">Three simple steps from POS to online store.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: "1", t: "Connect Your Inventory", d: "Your existing products sync from RetailPulse POS to your online store automatically." },
              { n: "2", t: "Customise Your Storefront", d: "Add your logo, colours, and a custom domain. Your store, your brand." },
               { n: "3", t: "Start Selling Online", d: "Share your link, accept orders, and manage them alongside your POS sales." },
            ].map(s => (
              <div key={s.n} className="text-center">
                <div className="w-12 h-12 rounded-full bg-indigo-500 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">{s.n}</div>
                <h3 className="font-semibold text-white mb-2">{s.t}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist form */}
      <section id="waitlist" className="py-20 bg-white">
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-4 py-1.5 text-sm mb-4">
              <TrendingUp className="w-3.5 h-3.5" /> Early access — limited spots
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Get Early Access</h2>
             <p className="text-slate-500">Be first in line when we launch. Storefront access will be available at no extra charge.</p>
          </div>

          {status === "success" ? (
            <div
              style={{
                background: "linear-gradient(to bottom right, #0a3d62, #3c6382)",
                color: "white",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                padding: "50px 20px",
                marginTop: "-80px",
                marginLeft: "calc(-50vw + 50%)",
                marginRight: "calc(-50vw + 50%)",
                width: "100vw",
              }}
            >
              <img src={LOGO} alt="RetailPulsePOS Logo" style={{ width: 120, marginBottom: 20, borderRadius: 16 }} />
              <h1 style={{ fontSize: "2.2em", marginBottom: 15, color: "#f6b93b" }}>
                Hello {form.name}, thank you for joining!
              </h1>
              <p style={{ fontSize: "1.2em", marginBottom: 20 }}>
                You're now on the <strong>RetailPulsePOS E‑Commerce Waitlist</strong>.
              </p>
              <p style={{ fontSize: "1em", marginBottom: 30, color: "#dff9fb" }}>
                We'll notify you by email and SMS once the platform launches.<br />
                Together, we're empowering retailers and communities online.
              </p>
              <Link href="/signup">
                <a
                  style={{
                    background: "#f6b93b",
                    color: "#0a3d62",
                    fontWeight: "bold",
                    padding: "12px 25px",
                    borderRadius: 5,
                    textDecoration: "none",
                    display: "inline-block",
                  }}
                >
                  Start Your Free POS Trial →
                </a>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Your Name</label>
                <Input
                  placeholder="Jane Adeyemi"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Email Address</label>
                <Input
                  type="email"
                  placeholder="jane@myboutique.ng"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Business Type</label>
                <select
                  value={form.businessType}
                  onChange={e => setForm(p => ({ ...p, businessType: e.target.value }))}
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select your business type...</option>
                  {BUSINESS_TYPES.map(bt => (
                    <option key={bt.value} value={bt.value}>{bt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Community / Association <span className="text-slate-400 font-normal">(optional)</span></label>
                <Input
                  placeholder="e.g. Alaba Traders, Computer Village, Lagos Market..."
                  value={form.community}
                  onChange={e => setForm(p => ({ ...p, community: e.target.value }))}
                />
                <p className="text-xs text-slate-400 mt-1">Which trader community, market, or association do you belong to?</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Anything you'd like us to know? <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  placeholder="e.g. I sell children's clothing in Lagos and want to ship nationwide..."
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  rows={3}
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              {status === "error" && (
                <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">{errorMsg}</p>
              )}
              <Button type="submit" className="w-full gap-2" disabled={status === "loading"}>
                {status === "loading" ? "Saving your spot..." : <>Join Waitlist <ArrowRight className="w-4 h-4" /></>}
              </Button>
              <p className="text-xs text-center text-slate-400">No spam. We'll only email you about the launch and early access.</p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src={LOGO} alt="RetailPulse" className="w-7 h-7 rounded-lg object-cover" />
            <span className="font-semibold text-slate-700">RetailPulse</span>
          </div>
          <p className="text-sm text-slate-400">© {new Date().getFullYear()} RetailPulse · POS & E-Commerce for Nigerian Retailers</p>
          <Link href="/signup">
            <Button size="sm" variant="outline" className="text-xs">Start Free POS Trial</Button>
          </Link>
        </div>
      </footer>
    </div>
  );
}
